import { CSSProperties, HTMLProps } from 'react';
import styled, { css } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { BreakpointProp } from '../../utils/styles/breakpoints';
import { Flex, FlexProps } from '../layout/Flex';

export type ButtonColor = 'primary' | 'invert' | 'secondary' | 'white' | 'hardLight' | 'accent';

export interface ButtonProps
    extends FlexProps,
        Omit<HTMLProps<HTMLButtonElement>, 'color' | 'size' | 'width' | 'height'> {
    size?: 'xs' | 's' | 'm' | 'l' | 'xl';
    color?: ButtonColor;
    variant?: 'filled' | 'outlined' | 'blur' | 'transparent';
    isHoverEnabled?: boolean;
}

type ButtonSchema = {
    color: CSSProperties['color'];
    backgrounds: {
        default?: CSSProperties['backgroundColor'];
        hover: CSSProperties['backgroundColor'];
    };
};

const FILLED_BUTTON_SCHEMAS: Record<ButtonColor, ButtonSchema> = {
    primary: {
        color: 'var(--control-primary-color)',
        backgrounds: {
            default: 'var(--control-primary-background)',
            hover: 'var(--control-primary-background-hover)',
        },
    },

    secondary: {
        color: 'var(--control-secondary-color)',
        backgrounds: {
            default: 'var(--control-secondary-background)',
            hover: 'var(--control-secondary-background-hover)',
        },
    },

    invert: {
        color: 'var(--control-invert-color)',
        backgrounds: {
            default: 'var(--control-invert-background)',
            hover: 'var(--control-invert-background-hover)',
        },
    },

    white: {
        color: '#000000',
        backgrounds: {
            default: '#FFFFFF',
            hover: '#F2F2F2',
        },
    },

    hardLight: {
        color: '#000000',
        backgrounds: {
            default: 'rgba(255, 255, 255, 0.65)',
            hover: 'rgba(255, 255, 255, 0.8)',
        },
    },

    accent: {
        color: 'var(--always-white)',
        backgrounds: {
            default: 'var(--accent)',
            hover: 'var(--accent-purple)',
        },
    },
};

const BLUR_BUTTON_SCHEMAS: Record<ButtonColor, ButtonSchema> = {
    primary: {
        color: 'var(--control-primary-blur-color)',
        backgrounds: {
            default: 'var(--control-primary-blur-background)',
            hover: 'var(--control-primary-blur-background-hover)',
        },
    },

    secondary: {
        color: 'var(--control-secondary-blur-color)',
        backgrounds: {
            default: 'var(--control-secondary-blur-background)',
            hover: 'var(--control-secondary-blur-background-hover)',
        },
    },

    invert: {
        color: 'var(--control-primary-blur-color)',
        backgrounds: {
            default: 'var(--control-primary-blur-background)',
            hover: 'var(--control-primary-blur-background-hover)',
        },
    },

    white: {
        color: 'var(--control-primary-blur-color)',
        backgrounds: {
            default: 'var(--control-primary-blur-background)',
            hover: 'var(--control-primary-blur-background-hover)',
        },
    },

    hardLight: {
        color: 'var(--control-primary-blur-color)',
        backgrounds: {
            default: 'var(--control-primary-blur-background)',
            hover: 'var(--control-primary-blur-background-hover)',
        },
    },

    accent: {
        color: 'var(--always-white)',
        backgrounds: {
            default: 'var(--accent)',
            hover: 'var(--accent-purple)',
        },
    },
};

const makeButtonFilledStyles = (schema: ButtonSchema, isHoverEnabled: boolean = true) => css`
    color: ${schema.color};
    background-color: ${schema.backgrounds.default ?? 'transparent'};
    font-weight: 700;

    ${isHoverEnabled
        ? `
            &:not([disabled]):active,
            &:not([disabled]).active,
            &:not([disabled]):hover,
            &:not([disabled]):focus {
                background-color: ${schema.backgrounds.hover};
            }`
        : ''}
`;

const makeButtonBlurStyles = (schema: ButtonSchema, isHoverEnabled: boolean = true) => css`
    color: ${schema.color};
    background-color: ${schema.backgrounds.default ?? 'transparent'};
    font-weight: 700;
    backdrop-filter: blur(50px);
    -webkit-backdrop-filter: blur(50px); /* Для поддержки в Safari */

    ${isHoverEnabled
        ? `
            &:not([disabled]):active,
            &:not([disabled]).active,
            &:not([disabled]):hover,
            &:not([disabled]):focus {
                background-color: ${schema.backgrounds.hover};
            }`
        : ''}
`;

const makeButtonOutlinedStyles = () => css`
    border: 1px solid var(--control-outline-background);
    background-color: transparent;
    color: var(--control-outline-color);

    &:hover {
        border-color: var(--control-outline-background-hover);
    }
`;

const makeButtonTransparentStyles = () => css`
    background-color: transparent;
    color: var(--color-primary);

    &:hover {
        color: var(--color-secondary);
    }
`;

const getNumberCssProp = (value: BreakpointProp<CSSProperties['height' | 'padding']>) => {
    if (typeof value === 'number') {
        return value + 'px';
    }

    if (typeof value === 'string') {
        return value;
    }
};

export const Button = styled(Flex)
    .withConfig({
        displayName: 'Button',
        shouldForwardProp: shouldIgnoreProp('width', 'size', 'color'),
    })
    .attrs((props) => ({
        margin: props.margin || '0',
    }))<ButtonProps>`
    box-sizing: border-box;
    display: inline-flex;
    -webkit-tap-highlight-color: transparent;
    background-color: transparent;
    outline: 0;
    border: 0;
    cursor: pointer;
    user-select: none;
    appearance: none;
    text-decoration: none;
    text-align: center;
    font-family: inherit;

    &[disabled] {
        color: var(--control-disabled-color);
        background-color: var(--control-disabled-background);
        cursor: not-allowed;
    }

    text-transform: uppercase;
    transition-property: color, background-color, border-color;
    transition-duration: 0.15s;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 400;

    ${(props) =>
        props.variant === 'filled' &&
        makeButtonFilledStyles(
            FILLED_BUTTON_SCHEMAS[props.color ?? 'primary'],
            props.isHoverEnabled,
        )}

    ${(props) =>
        props.variant === 'blur' &&
        makeButtonBlurStyles(BLUR_BUTTON_SCHEMAS[props.color ?? 'primary'], props.isHoverEnabled)}

    ${(props) => props.variant === 'outlined' && makeButtonOutlinedStyles()}

     ${(props) => props.variant === 'transparent' && makeButtonTransparentStyles()}

    ${(props) =>
        props.size === 'xs' &&
        css`
            font-size: 10px;
            line-height: 12px;
            height: ${props.height ? getNumberCssProp(props.height) : '28px'};
            padding: ${getNumberCssProp(props.padding) || '0 16px'};
        `}

    ${(props) =>
        props.size === 's' &&
        css`
            font-size: 12px;
            line-height: 14px;
            height: ${props.height ? getNumberCssProp(props.height) : '32px'};
            padding: ${getNumberCssProp(props.padding) || '0 16px'};
        `}

    ${(props) =>
        props.size === 'm' &&
        css`
            font-size: 12px;
            line-height: 14px;
            height: ${props.height ? getNumberCssProp(props.height) : '40px'};
            padding: ${getNumberCssProp(props.padding) || '0 16px'};
        `}

    ${(props) =>
        props.size === 'l' &&
        css`
            font-size: 12px;
            line-height: 14px;
            height: ${props.height ? getNumberCssProp(props.height) : '48px'};
            padding: ${getNumberCssProp(props.padding) || '0 16px'};
        `}

    ${(props) =>
        props.size === 'xl' &&
        css`
            font-size: 16px;
            line-height: 19px;
            height: ${props.height ? getNumberCssProp(props.height) : '56px'};
            padding: ${getNumberCssProp(props.padding) || '0 16px'};
        `}

    ${(props) =>
        props.textTransform &&
        css`
            text-transform: ${props.textTransform};
        `}

    ${(props) =>
        props.textAlign &&
        css`
            text-align: ${props.textAlign};
        `}
`;

Button.defaultProps = {
    as: 'button',
    position: 'relative',
    size: 's',
    variant: 'filled',
    direction: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1000,
    isHoverEnabled: true,
};
